import { useState } from 'react'
import type { FeedbackClaim } from '../lib/api.ts'
import { TRIAGE_VERDICT_HINT, TRIAGE_VERDICT_LABEL, label } from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { FOCUS_RING } from './focusRing.ts'

/**
 * Our classification of one piece of feedback (#405): concordo or discordo
 * with the conferente.
 *
 * Pending, it is the two buttons and an optional note, and each button says
 * what it will count as -- a concordo on "não é divergência" is a false
 * positive of ours, which is worth knowing before pressing it rather than
 * after. Classified, it says what was decided, by whom and when, and offers to
 * change it or send it back to pending: a classification is somebody's reading
 * of the feedback, and a second look is allowed to disagree with the first.
 */
export function TriageControls({
  claim,
  verdict,
  note,
  by,
  at,
  onSave,
  onClear,
  draftNote,
  onDraftChange,
  disabled = false,
}: {
  claim: FeedbackClaim
  verdict: string | null
  note: string | null
  by: string | null
  at: string | null
  onSave: (verdict: string, note: string) => Promise<void>
  onClear: () => Promise<void>
  draftNote?: string | undefined
  onDraftChange?: ((note: string) => void) | undefined
  disabled?: boolean | undefined
}) {
  const [editing, setEditing] = useState(false)
  const [localText, setLocalText] = useState(note ?? '')
  const text = draftNote ?? localText
  const setText = (value: string) => {
    setLocalText(value)
    onDraftChange?.(value)
  }
  const [saving, setBusy] = useState(false)
  const busy = saving || disabled
  const [error, setError] = useState<string | undefined>()

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(undefined)
    try {
      await action()
      setEditing(false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'não foi possível salvar')
    } finally {
      setBusy(false)
    }
  }

  const hint = (key: string) => TRIAGE_VERDICT_HINT[key]?.[claim] ?? ''

  if (verdict !== null && !editing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`text-tPSemi ${verdict === 'agree' ? 'text-text-strong' : 'text-text-weak'}`}
        >
          {label(TRIAGE_VERDICT_LABEL, verdict)}
        </span>
        <span className="text-tSmall text-text-weak">{hint(verdict)}</span>
        {note !== null && note !== '' && <span className="text-tP text-text-weak">— {note}</span>}
        <span className="text-tSmall text-text-weak">
          {by ?? ''}
          {at === null ? '' : ` · ${new Date(at).toLocaleString('pt-BR')}`}
        </span>
        <span className="grow" />
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          disabled={busy}
          onClick={() => {
            setText(note ?? '')
            setEditing(true)
          }}
        >
          Alterar
        </Button>
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          disabled={busy}
          onClick={() => void run(onClear)}
        >
          Voltar para pendente
        </Button>
        {error !== undefined && <p className="text-tSmall text-text-error w-full">{error}</p>}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <textarea
        value={text}
        rows={2}
        maxLength={2000}
        disabled={busy}
        aria-label="Nota da classificação"
        placeholder="Nota (opcional)"
        onChange={(event) => setText(event.target.value)}
        className={`bg-fill-white border-stroke-strong text-tP text-text-strong w-full resize-none rounded-[var(--radius-base)] border px-3 py-2 ${FOCUS_RING}`}
      />
      <div className="flex flex-wrap items-start gap-3">
        {Object.keys(TRIAGE_VERDICT_LABEL).map((key) => (
          <div key={key} className="flex max-w-[18rem] flex-col gap-0.5">
            <Button
              variant={key === 'agree' ? 'primary' : 'secondary'}
              tone={key === 'agree' ? 'brand' : 'neutral'}
              size="small"
              disabled={busy}
              onClick={() => void run(() => onSave(key, text))}
            >
              {label(TRIAGE_VERDICT_LABEL, key)}
            </Button>
            <span className="text-tSmall text-text-weak">{hint(key)}</span>
          </div>
        ))}
        {editing && (
          <Button
            variant="tertiary"
            tone="neutral"
            size="small"
            disabled={busy}
            onClick={() => setEditing(false)}
          >
            Cancelar
          </Button>
        )}
        {error !== undefined && <p className="text-tSmall text-text-error">{error}</p>}
      </div>
    </div>
  )
}
